import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { PageHeader, Panel, Button, FormRow, Textarea, DataTable, Chip, Banner, Checkbox, Drawer, StatTile, Select, SearchInput, useToast, type Column } from '../ds/index'
import { useApi } from '../lib/useApi'
import { post } from '../lib/api'
import { ago, num } from '../lib/format'
import { LoadingBlock, ErrorBlock } from '../components/Loading'

type Email = {
  id: string; received_at: string; from_address: string | null; subject: string | null; kind: string; has_post_link: boolean; has_group: boolean
  truncated: boolean; group_name: string | null; post_key: string | null; post_id: string | null; inbox: string; post_stage: string | null; post_copies: number | null
  parse: { notes?: string[]; text?: string | null }; body_purged_at: string | null
  held: boolean; rep_id: string | null; rep_name: string | null; post_summary: string | null
  category_id: string | null; category_name: string | null; area_name: string | null; post_sold: number | null
}

/** "Facebook <notification@facebookmail.com>" reads as "Facebook"; a bare address stays as it is. */
const senderLabel = (from: string | null) => {
  if (!from) return 'unknown'
  const name = from.replace(/<[^>]*>/, '').replace(/"/g, '').trim()
  return name || from.replace(/[<>]/g, '')
}

// What became of the email, in one word a person can sort by.
type Outcome = 'held' | 'sold' | 'lead' | 'not_lead' | 'waiting' | 'no_post'
function outcomeOf(e: Email): Outcome {
  if (e.held) return 'held'
  if (!e.post_id) return 'no_post'
  if (e.post_sold) return 'sold'
  if (e.post_stage === 'routed' || e.post_stage === 'lead') return 'lead'
  if (e.post_stage === 'not_lead') return 'not_lead'
  return 'waiting'
}
const OUTCOME: Record<Outcome, { label: string; tone?: 'pos' | 'warn' | 'neg' | 'accent' }> = {
  held: { label: 'Held: sender unknown', tone: 'warn' },
  sold: { label: 'Lead, sold', tone: 'pos' },
  lead: { label: 'Lead', tone: 'accent' },
  not_lead: { label: 'Not a lead' },
  waiting: { label: 'Held or waiting', tone: 'warn' },
  no_post: { label: 'No post' },
}
type Result = {
  duplicate_email?: boolean; kind: string; new_post?: boolean; stage?: string
  parse: { notes: string[]; group_name: string | null; post_url: string | null; author_display: string | null; text: string | null; truncated: boolean }
  post: { stage: string; category_name: string | null; area_name: string | null; summary: string | null; screen_reason: string | null; route_note: string | null; is_lead: boolean | null } | null
}

const KIND: Record<string, { label: string; tone?: 'pos' | 'warn' | 'neg' }> = {
  group_post: { label: 'Group post', tone: 'pos' },
  digest: { label: 'Digest', tone: 'warn' },
  other_facebook: { label: 'Other Facebook' },
  not_facebook: { label: 'Not Facebook', tone: 'neg' },
}

export default function Intake() {
  type Totals = { emails: number; group_post: { all: number; week: number; today: number }; linked: { all: number; week: number }; digest: { all: number; week: number }; other: { all: number; week: number } }
  const list = useApi<{ emails: Email[]; totals: Totals }>('/api/intake/emails')
  const mailbox = useApi<{ setup: { ready: boolean }; inboxes: { address: string; status: string; last_polled_at: string | null }[] }>('/api/mailbox')
  const [testOpen, setTestOpen] = useState<boolean | null>(null)
  const [raw, setRaw] = useState('')
  const [process, setProcess] = useState(true)
  const [busy, setBusy] = useState(false)
  const [result, setResult] = useState<Result | null>(null)
  const [err, setErr] = useState<string | null>(null)
  const [sel, setSel] = useState<Email | null>(null)
  const toast = useToast()

  const submit = async () => {
    setBusy(true); setErr(null); setResult(null)
    try {
      const r = await post<Result>('/api/intake/email', { raw, process })
      setResult(r)
      if (!r.duplicate_email) { setRaw(''); list.reload() }
      toast(r.duplicate_email ? 'Already had that email' : 'Email read', r.duplicate_email ? 'warn' : 'pos')
    } catch (e) { setErr((e as Error).message) } finally { setBusy(false) }
  }

  // Sorting and filtering run on the loaded list; the server sends the newest 500.
  const [q, setQ] = useState('')
  const [fKind, setFKind] = useState('')
  const [fRep, setFRep] = useState('')
  const [fCat, setFCat] = useState('')
  const [fOut, setFOut] = useState('')
  const all = list.data?.emails || []
  const reps = useMemo(() => [...new Map(all.filter((e) => e.rep_id).map((e) => [e.rep_id, e.rep_name])).entries()].sort((a, b) => String(a[1]).localeCompare(String(b[1]))), [all])
  const cats = useMemo(() => [...new Map(all.filter((e) => e.category_id).map((e) => [e.category_id, e.category_name])).entries()].sort((a, b) => String(a[1]).localeCompare(String(b[1]))), [all])
  const shown = all.filter((e) => {
    if (fKind && e.kind !== fKind) return false
    if (fRep === 'none' ? e.rep_id : fRep && e.rep_id !== fRep) return false
    if (fCat === 'none' ? e.category_id : fCat && e.category_id !== fCat) return false
    if (fOut && outcomeOf(e) !== fOut) return false
    if (q) {
      const hay = `${e.subject || ''} ${e.group_name || ''} ${e.from_address || ''} ${e.rep_name || ''} ${e.post_summary || ''} ${e.category_name || ''}`.toLowerCase()
      if (!hay.includes(q.toLowerCase())) return false
    }
    return true
  })
  const filtering = Boolean(q || fKind || fRep || fCat || fOut)

  const cols: Column<Email>[] = [
    { key: 'when', header: 'Received', cell: (r) => <span className="nowrap muted">{ago(r.received_at)}</span>, sortBy: (r) => r.received_at },
    { key: 'from', header: 'From', cell: (r) => r.rep_name ? <strong className="nowrap">{r.rep_name}</strong> : <span className="muted nowrap" title={r.from_address || ''}>{senderLabel(r.from_address)}</span>, sortBy: (r) => r.rep_name || `~${r.from_address || ''}` },
    { key: 'subject', header: 'Subject', cell: (r) => <><span>{r.subject || <span className="muted">(purged)</span>}</span><div className="muted nowrap">{r.group_name || r.inbox}</div></>, sortBy: (r) => r.group_name || r.subject || '' },
    { key: 'kind', header: 'Kind', cell: (r) => <Chip tone={KIND[r.kind]?.tone} dot>{KIND[r.kind]?.label || r.kind}</Chip>, sortBy: (r) => r.kind },
    { key: 'cat', header: 'Category', cell: (r) => r.category_name ? <span className="nowrap">{r.category_name}{r.area_name ? <span className="muted"> · {r.area_name}</span> : null}</span> : <span className="muted">none</span>, sortBy: (r) => r.category_name || '~' },
    { key: 'checks', header: 'Link / group / full text', cell: (r) => r.kind === 'group_post' ? (
      <span className="nowrap">{r.has_post_link ? 'yes' : <strong>no</strong>} / {r.has_group ? 'yes' : <strong>no</strong>} / {r.truncated ? <strong>cut</strong> : 'yes'}</span>
    ) : <span className="muted">n/a</span> },
    { key: 'post', header: 'Became', cell: (r) => { const o = OUTCOME[outcomeOf(r)]; return <span className="nowrap"><Chip tone={o.tone} dot>{o.label}</Chip>{r.post_copies && r.post_copies > 1 ? <span className="muted"> · {r.post_copies} copies</span> : ''}</span> }, sortBy: (r) => outcomeOf(r) },
  ]

  const inbox = mailbox.data?.inboxes.find((i) => i.status !== 'not_connected') || null
  const connected = inbox?.status === 'active' || inbox?.status === 'error'
  const showTest = testOpen ?? (mailbox.data ? !connected : false)

  return (
    <>
      <PageHeader title="Emails in" subtitle="Every Facebook notification email that reached the platform, what the reader made of it, and the post it became." />
      {mailbox.data && (
        <div style={{ marginBottom: 'var(--s-6)' }}>
          {connected && inbox ? (
            <Banner tone="pos" title={`Connected to ${inbox.address}`} action={<Link className="btn btn-ghost" to="/mailbox">Mailbox</Link>}>
              New emails arrive here on their own. The mailbox is checked every minute{inbox.last_polled_at ? `, last ${ago(inbox.last_polled_at)}` : ''}.
            </Banner>
          ) : inbox?.status === 'needs_reconnect' ? (
            <Banner tone="neg" title="The mailbox connection is broken" action={<Link className="btn btn-ghost" to="/mailbox">Fix it</Link>}>
              Nothing new arrives until someone connects {inbox.address} again.
            </Banner>
          ) : (
            <Banner tone="warn" title="Not connected to an inbox yet" action={<Link className="btn btn-ghost" to="/mailbox">Mailbox</Link>}>
              Alerts are meant to arrive here on their own from the mailbox reps' Muse emails. That connection is built but switched off until the mailbox exists, so nothing arrives by itself yet. Until then, the only way an email gets in is testing one by hand at the bottom of this page.
            </Banner>
          )}
        </div>
      )}
      {list.data && (() => {
        // Every email, not only the 500 the table below lists.
        const t = list.data.totals
        const gp = t.group_post
        const share = gp.week ? (t.linked.week / gp.week) * 100 : null
        return (
          <div className="grid-tiles" style={{ marginBottom: 'var(--s-6)' }}>
            <StatTile focal label="Group post emails today" value={num(gp.today)} meta={`${num(gp.week)} in the last 7 days, ${num(gp.all)} in all`} />
            <StatTile label="With a post link" value={share != null ? `${Math.round(share)}%` : 'n/a'} meta={`${num(t.linked.week)} of ${num(gp.week)} in the last 7 days`} progress={share ?? undefined} />
            <StatTile label="Digests" value={num(t.digest.week)} meta={`several posts in one email, last 7 days (${num(t.digest.all)} in all)`} />
            <StatTile label="Other or not Facebook" value={num(t.other.week)} meta={`comments, alerts, anything else, last 7 days (${num(t.other.all)} in all)`} />
          </div>
        )
      })()}
      <Panel className="panel-flush">
        <div className="panel-header" style={{ padding: 'var(--s-5) var(--s-6) 0' }}><h2 className="panel-title">Recent emails</h2><span className="muted">{filtering ? `${shown.length} of ${all.length}` : `latest ${all.length}`}. Click a column to sort.</span></div>
        <div className="lead-filters-row" style={{ padding: 'var(--s-4) var(--s-6) 0' }}>
          <SearchInput aria-label="Search emails" placeholder="Search subject, group, sender, request" value={q} onChange={(e) => setQ(e.target.value)} />
          <Select aria-label="Rep" value={fRep} onChange={(e) => setFRep(e.target.value)} className={fRep ? 'is-set' : undefined}>
            <option value="">From: anyone</option><option value="none">Not tied to a rep</option>
            {reps.map(([id, name]) => <option key={String(id)} value={String(id)}>{name}</option>)}
          </Select>
          <Select aria-label="Category" value={fCat} onChange={(e) => setFCat(e.target.value)} className={fCat ? 'is-set' : undefined}>
            <option value="">Category: all</option><option value="none">No category</option>
            {cats.map(([id, name]) => <option key={String(id)} value={String(id)}>{name}</option>)}
          </Select>
          <Select aria-label="What it became" value={fOut} onChange={(e) => setFOut(e.target.value)} className={fOut ? 'is-set' : undefined}>
            <option value="">Became: anything</option>
            {(Object.keys(OUTCOME) as Outcome[]).map((k) => <option key={k} value={k}>{OUTCOME[k].label}</option>)}
          </Select>
          <Select aria-label="Kind" value={fKind} onChange={(e) => setFKind(e.target.value)} className={fKind ? 'is-set' : undefined}>
            <option value="">Kind: all</option>
            {Object.entries(KIND).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </Select>
          {filtering && <Button variant="ghost" onClick={() => { setQ(''); setFKind(''); setFRep(''); setFCat(''); setFOut('') }}>Clear</Button>}
        </div>
        {list.error && <ErrorBlock error={list.error} />}
        {!list.data ? <div style={{ padding: 'var(--s-6)' }}><LoadingBlock /></div> : <DataTable columns={cols} rows={shown} rowKey={(r) => r.id} onRowClick={setSel} />}
      </Panel>
      <div style={{ height: 'var(--s-6)' }} />
      {showTest ? (
        <>
      <div className="two-col">
        <Panel>
          <div className="panel-header"><h2 className="panel-title">Test one email by hand</h2></div>
          <div className="stack-tight">
            <span className="muted" style={{ fontSize: 'var(--t-ui)' }}>For checking how a single email reads, without waiting for the mailbox. Real emails do not need this. In Gmail, open the notification, choose <strong>Show original</strong>, copy everything, and paste it here.</span>
            <FormRow label="Raw email" htmlFor="raw-email"><Textarea id="raw-email" className="raw-paste" value={raw} onChange={(e) => setRaw(e.target.value)} placeholder="Delivered-To: ...&#10;From: Facebook <notification@facebookmail.com>&#10;Subject: ..." /></FormRow>
            <Checkbox label="Screen and route it now" checked={process} onChange={(e) => setProcess(e.target.checked)} />
            {err && <p className="form-error" role="alert">{err}</p>}
            <div><Button onClick={submit} disabled={!raw.trim() || busy}>{busy ? 'Reading' : 'Read this email'}</Button></div>
          </div>
        </Panel>
        <Panel>
          <div className="panel-header"><h2 className="panel-title">What the reader saw</h2></div>
          {!result ? <span className="muted">Test an email to see how it reads: kind, group, post link, text, and the screening result.</span> : (
            <div className="stack-tight">
              {result.duplicate_email && <Banner tone="warn" title="Already received">This exact email was pasted before. Nothing changed.</Banner>}
              <div className="row-tight"><Chip tone={KIND[result.kind]?.tone} dot>{KIND[result.kind]?.label}</Chip>{result.new_post === false && <Chip>Post already known, copy counted</Chip>}</div>
              <ul className="notes">{result.parse.notes.map((n, i) => <li key={i}>{n}</li>)}</ul>
              {result.kind === 'group_post' && (
                <div className="post-card">
                  <div className="post-meta"><strong>{result.parse.group_name || 'no group'}</strong><span>{result.parse.author_display || 'no author'}</span></div>
                  <p className="post-body">{result.parse.text || <span className="muted">No text survived.</span>}</p>
                  {result.parse.truncated && <div className="post-cut">Cut off in the email.</div>}
                  <span className="muted" style={{ fontSize: 'var(--t-ui)', wordBreak: 'break-all' }}>{result.parse.post_url || 'No post link'}</span>
                </div>
              )}
              {result.post && (
                <Banner tone={result.post.is_lead ? 'pos' : 'neutral'} title={result.post.stage === 'routed' ? `Lead: ${result.post.category_name}${result.post.area_name ? `, ${result.post.area_name}` : ''}` : result.post.stage.replace('_', ' ')}>
                  {result.post.summary || result.post.screen_reason}{result.post.route_note ? ` (${result.post.route_note})` : ''}
                </Banner>
              )}
            </div>
          )}
        </Panel>
      </div>
        </>
      ) : (
        <div><Button variant="secondary" onClick={() => setTestOpen(true)}>Test one email by hand</Button></div>
      )}
      {sel && (
        <Drawer title={sel.subject || 'Email'} onClose={() => setSel(null)}>
          <div className="stack-tight">
            <Chip tone={KIND[sel.kind]?.tone} dot>{KIND[sel.kind]?.label}</Chip>
            <span className="muted">{sel.rep_name ? `${sel.rep_name} (${sel.from_address})` : sel.from_address} · {sel.inbox} · {new Date(sel.received_at).toLocaleString()}</span>
            {sel.post_id && <Link to={`/leads/${sel.post_id}?view=all`}>Open the post this became</Link>}
            <ul className="notes">{(sel.parse.notes || []).map((n, i) => <li key={i}>{n}</li>)}</ul>
            {sel.parse.text && <p className="post-body">{sel.parse.text}</p>}
          </div>
        </Drawer>
      )}
    </>
  )
}
